/**
 * DistributionHistory - Past periods with payout info for holders
 */

import React from 'react';
import { Badge } from '../../ui/components/Badge';
import { usePublicDistributionHistory } from '../../hooks/useDistribution';
import type { DistributionPeriod } from '../../api/distribution';

interface DistributionHistoryProps {
  projectSlug: string;
  myPastRewards?: Array<{
    period_id?: string;
    period_number: number;
    period_status: string;
    period_started_at: string | null;
    period_ended_at: string | null;
    period_distributed_at: string | null;
    points: number;
    payout_amount: number;
  }>;
  rewardCurrencyLabel?: string;
}

export const DistributionHistory: React.FC<DistributionHistoryProps> = ({
  projectSlug,
  myPastRewards = [],
  rewardCurrencyLabel = 'COREUM',
}) => {
  const { data, isLoading } = usePublicDistributionHistory(projectSlug);
  const periods = data?.periods || [];

  if (isLoading) {
    return <div className="text-subtext-color py-4">Loading history...</div>;
  }

  if (periods.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <span className="text-body-bold font-body-bold text-default-font">Distribution History</span>
      <div className="flex flex-col gap-2">
        {periods.map(period => {
          const myReward = myPastRewards.find(r => r.period_number === period.period_number);

          return (
            <div
              key={period.id}
              className="flex items-center justify-between rounded border border-solid border-neutral-border p-3"
            >
              <div className="flex items-center gap-3">
                <Badge variant={period.status === 'distributed' ? 'success' : 'neutral'}>
                  {period.status === 'pending_distribution' ? 'pending' : period.status}
                </Badge>
                <span className="text-body font-body text-default-font">
                  Period #{period.period_number}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {period.ended_at ? new Date(period.ended_at).toLocaleDateString() : '-'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-caption font-caption text-subtext-color">
                {period.total_eligible_wallets != null && (
                  <span>{period.total_eligible_wallets} recipients</span>
                )}
                {myReward && (
                  <span className="text-default-font font-body-bold">
                    {Number(myReward.payout_amount).toLocaleString(undefined, { maximumFractionDigits: 4 })} {rewardCurrencyLabel}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
